import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import { usePublicSectionTheme } from "@/lib/publicTheme";
import { Briefcase, MapPin, ArrowRight, Building2, Clock, Wallet, UserPlus, ChevronLeft, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { formatCompCurrency } from "@/lib/budget";

const publicApi = axios.create({ baseURL: API });

const DEFAULT_LIST_FIELDS = ["title", "urgency", "location", "work_mode", "employment_type", "experience", "department", "skills", "apply_cta"];

/** Custom embed board renderer — reads theme + filters + tabs + field config from the board API. */
export default function EmbedBoardV2() {
    const { boardId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(""); // "" = All
    const [view, setView] = useState("jobs"); // "jobs" | "pool"
    const rootRef = useRef(null);

    // Employer's "embed" section theme override (if any).
    usePublicSectionTheme(data?.company?.id, "embed");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/public/embed-boards/${boardId}/jobs`, { withCredentials: false });
                setData(data);
            } catch (err) { setError(formatApiError(err)); }
            finally { setLoading(false); }
        })();
    }, [boardId]);

    // Auto-resize parent iframe to fit content — uses ResizeObserver + MutationObserver
    // + fonts/images load + a continuous heartbeat poll so the host always gets the correct
    // height even after late DOM growth, layout reflows from grid breakpoint changes on
    // mobile, or async transitions/animations the observers don't reliably catch.
    useLayoutEffect(() => {
        // Lock iframe document scroll — prevents content "middle slice"
        // cropping when an input/dropdown gets focused inside the iframe.
        const lock = () => { if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0); };
        const prevHtmlOverflow = document.documentElement.style.overflow;
        const prevBodyOverflow = document.body.style.overflow;
        document.documentElement.style.overflow = "visible";
        document.body.style.overflow = "visible";
        window.addEventListener("scroll", lock, { passive: true });

        let last = 0;
        const measure = () => {
            if (!rootRef.current) return 0;
            // IMPORTANT: only measure the root's own height. Including
            // `document.body.scrollHeight` or `document.documentElement.scrollHeight`
            // creates a feedback loop — the parent grows the iframe to the
            // reported height, which then becomes the new body scrollHeight,
            // which we'd report back as larger, ad infinitum.
            return Math.ceil(rootRef.current.getBoundingClientRect().height) + 16;
        };
        const post = () => {
            const h = measure();
            if (h <= 0 || Math.abs(h - last) < 2) return;
            last = h;
            try { window.parent?.postMessage({ kind: "portalwiz:resize", type: "portalwiz-resize", target: boardId, height: h }, "*"); } catch { /* ignore */ }
        };
        post();
        const ro = new ResizeObserver(post);
        if (rootRef.current) ro.observe(rootRef.current);
        const mo = new MutationObserver(post);
        if (rootRef.current) mo.observe(rootRef.current, { subtree: true, childList: true, attributes: true, characterData: true });
        window.addEventListener("load", post);
        window.addEventListener("resize", post);
        window.addEventListener("transitionend", post, true);
        window.addEventListener("animationend", post, true);
        try { document.fonts?.ready?.then(post); } catch { /* ignore */ }
        const imgs = rootRef.current ? rootRef.current.querySelectorAll("img") : [];
        imgs.forEach((img) => img.addEventListener("load", post));
        // Heartbeat — guarantees the iframe always converges on the right height
        // even if every observer somehow misses a state change. Cheap: post()
        // early-returns when nothing has changed.
        const heartbeat = setInterval(post, 800);
        return () => {
            ro.disconnect();
            mo.disconnect();
            window.removeEventListener("load", post);
            window.removeEventListener("scroll", lock);
            window.removeEventListener("resize", post);
            window.removeEventListener("transitionend", post, true);
            window.removeEventListener("animationend", post, true);
            imgs.forEach((img) => img.removeEventListener("load", post));
            clearInterval(heartbeat);
            document.documentElement.style.overflow = prevHtmlOverflow;
            document.body.style.overflow = prevBodyOverflow;
        };
    }, [data, boardId, activeTab, loading, view]);

    const board = data?.board;
    const t = board?.theme || {};
    const showPB = board?.show_powered_by !== false;
    const layout = board?.layout || "list";
    const listFields = (board?.list_fields && board.list_fields.length) ? board.list_fields : DEFAULT_LIST_FIELDS;
    const showField = (k) => listFields.includes(k);
    const customTabs = (board?.tabs || []).filter((tb) => tb && tb.title);

    // Auto-build department tabs ONLY when no custom tabs are configured
    const autoTabs = useMemo(() => {
        if (customTabs.length > 0) return [];
        if (!data?.jobs) return [];
        const map = new Map();
        for (const j of data.jobs) {
            const d = (j.department || "").trim();
            if (!d) continue;
            map.set(d, (map.get(d) || 0) + 1);
        }
        return [...map.entries()].sort((a, b) => b[1] - a[1]).map(([name, n]) => ({
            id: `auto-${name}`,
            title: `${name} · ${n}`,
            _autoDept: name,
        }));
    }, [data, customTabs.length]);

    const tabs = customTabs.length > 0 ? customTabs : autoTabs;

    const tabMatch = (job, tab) => {
        if (!tab) return true;
        if (tab._autoDept) return (job.department || "").trim() === tab._autoDept;
        const f = tab.filters || {};
        if (f.department && (job.department || "").trim().toLowerCase() !== f.department.trim().toLowerCase()) return false;
        if (f.work_mode && (job.work_mode || "").trim() !== f.work_mode) return false;
        if (f.employment_type && (job.employment_type || "").trim() !== f.employment_type) return false;
        if (f.client_id && job.client_id && job.client_id !== f.client_id) return false;
        if (f.search) {
            const s = f.search.toLowerCase();
            const hay = `${job.title || ""} ${(job.required_skills || []).join(" ")} ${job.department || ""}`.toLowerCase();
            if (!hay.includes(s)) return false;
        }
        return true;
    };

    const visible = useMemo(() => {
        if (!data?.jobs) return [];
        if (!activeTab) return data.jobs;
        const tab = tabs.find((x) => x.id === activeTab);
        if (!tab) return data.jobs;
        return data.jobs.filter((j) => tabMatch(j, tab));
    }, [data, activeTab, tabs]);

    // Feature: `spotlight_section_on_top` — when the board is configured
    // to lift highlighted jobs into a dedicated section, we split the
    // visible list here. Otherwise the whole list renders together
    // (highlighted jobs still float to the top thanks to server-side sort).
    const spotlightSplit = useMemo(() => {
        if (!board?.spotlight_section_on_top) return null;
        const spot = visible.filter((j) => j.highlight);
        if (spot.length === 0) return null;
        const other = visible.filter((j) => !j.highlight);
        return { spot, other };
    }, [board?.spotlight_section_on_top, visible]);

    const trackBranding = () => {
        try {
            const u = `${API}/public/embed-boards/${boardId}/branding-click`;
            navigator.sendBeacon ? navigator.sendBeacon(u) : fetch(u, { method: "POST", keepalive: true });
        } catch { /* ignore */ }
    };

    // Lightweight fire-and-forget event tracker for funnel analytics
    const postEvent = (kind, extra = {}) => {
        try {
            const url = `${API}/public/embed-boards/${boardId}/event`;
            const body = JSON.stringify({ kind, ...extra });
            if (navigator.sendBeacon) {
                const blob = new Blob([body], { type: "application/json" });
                navigator.sendBeacon(url, blob);
            } else {
                fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
            }
        } catch { /* ignore */ }
    };

    const onTabChange = (tabId) => {
        setActiveTab(tabId);
        if (tabId) postEvent("tab_view", { tab_id: tabId });
    };

    // When user clicks a job link, ask the parent page to scroll the iframe into view
    // BEFORE the navigation actually finishes — improves UX dramatically when the embed is far down the page.
    const requestScrollToTop = (jobId) => {
        try { window.parent?.postMessage({ kind: "portalwiz:scroll-to-top", target: boardId }, "*"); } catch { /* ignore */ }
        if (jobId) postEvent("job_click", { job_id: jobId, tab_id: activeTab || null });
    };

    const palette = {
        bg: t.bg || "#ffffff",
        card: t.card_bg || "#ffffff",
        border: t.border_color || "#e2e8f0",
        text: t.text_color || "#0f172a",
        sub: t.sub_text_color || "#64748b",
        accent: t.primary_color || "#002FA7",
    };

    // Inject Google Fonts stylesheets for non-system fonts (body + meta) so the
    // embed actually picks up the chosen families. Without this, browsers fall
    // back to Inter/sans-serif and the recruiter sees "font changes not
    // working" even though the inline style is set correctly.
    useEffect(() => {
        const sysFonts = new Set(["Inter", "system-ui", "sans-serif", "serif", "monospace", "ui-sans-serif", "ui-monospace", "ui-serif", "Arial", "Helvetica", "Georgia", "Times", "Courier"]);
        const loadFam = (family) => {
            const fam = (family || "").split(",")[0].trim().replace(/^['"]|['"]$/g, "");
            if (!fam || sysFonts.has(fam)) return;
            const id = `pw2-font-${fam.replace(/\s+/g, "-")}`;
            if (document.getElementById(id)) return;
            const link = document.createElement("link");
            link.id = id;
            link.rel = "stylesheet";
            link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fam).replace(/%20/g, "+")}:wght@300;400;500;600;700;800;900&display=swap`;
            document.head.appendChild(link);
        };
        loadFam(t.font_family);
        loadFam(t.meta_font);
    }, [t.font_family, t.meta_font]);

    return (
        <div
            ref={rootRef}
            style={{
                background: palette.bg, color: palette.text,
                fontFamily: t.font_family || "Inter, system-ui, sans-serif",
                ["--pw2-radius"]: t.card_radius || "0px",
                ["--pw2-btn-radius"]: t.button_radius || "0px",
                ["--pw2-accent"]: palette.accent,
            }}
            className="pw2-root"
            data-testid="embed-v2-root"
        >
            <style>{`
                .pw2-root { padding: 16px; }
                @media (min-width: 640px) { .pw2-root { padding: 20px 20px 24px; } }
                .pw2-card { transition: transform .15s ease, border-color .15s ease; display: block; border-radius: var(--pw2-radius); }
                .pw2-card:hover { transform: translateY(-1px); border-color: ${palette.accent}; }
                @keyframes pw2-pulse { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
                .pw2-skel { animation: pw2-pulse 1.4s ease-in-out infinite; background: ${palette.border}; height: 14px; border-radius: 2px; }
                .pw2-dept { padding: 5px 10px; font-size: 11px; font-family: ui-monospace, monospace; text-transform: uppercase; letter-spacing: .1em; border: 1px solid ${palette.border}; cursor: pointer; background: ${palette.card}; color: ${palette.sub}; white-space: nowrap; border-radius: var(--pw2-btn-radius); }
                .pw2-dept:hover { border-color: ${palette.accent}; color: ${palette.text}; }
                .pw2-dept.active { background: ${palette.accent}; color: #fff; border-color: ${palette.accent}; }
                .pw2-title { font-size: ${t.title_size || "30px"}; font-weight: ${t.title_weight || "900"}; line-height: 1.1; }
                .pw2-job-title { font-size: ${t.card_title_size || "17px"}; font-weight: ${t.card_title_weight || "800"}; }
                @media (max-width: 640px) {
                    .pw2-title { font-size: 22px; }
                    .pw2-job-title { font-size: 16px; }
                }
                .pw2-card-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; flex-wrap: wrap; }
                .pw2-meta-row { display: flex; gap: 8px 12px; flex-wrap: wrap; font-family: ${t.meta_font || "ui-monospace, monospace"}; font-size: ${t.meta_size || "11.5px"}; }
                .pw2-tabs-scroll { display: flex; gap: 6px; flex-wrap: nowrap; overflow-x: auto; padding-bottom: 4px; -webkit-overflow-scrolling: touch; }
                @media (min-width: 640px) { .pw2-tabs-scroll { flex-wrap: wrap; overflow-x: visible; padding-bottom: 0; } }
                .pw2-grid { display: grid; gap: 10px; grid-template-columns: 1fr; }
                @media (min-width: 640px) { .pw2-grid { grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); } }
                a, a:visited { color: ${palette.text}; text-decoration: none; }
            `}</style>
            {loading ? (
                <Skeleton palette={palette} />
            ) : error ? (
                <div style={{ color: "#dc2626", fontSize: 14 }} data-testid="embed-v2-error">{error}</div>
            ) : !data ? null : view === "pool" ? (
                <InlineTalentPool
                    board={board}
                    company={data.company}
                    palette={palette}
                    onBack={() => setView("jobs")}
                />
            ) : (
                <>
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 10.5, fontFamily: "ui-monospace, monospace", textTransform: "uppercase", letterSpacing: ".16em", color: palette.accent }}>Open roles at</div>
                        <h1 className="pw2-title" style={{ letterSpacing: "-0.02em", margin: "4px 0 0 0" }}>{t.title || data.company?.name || "Open roles"}</h1>
                        <div style={{ marginTop: 4, fontSize: 12, color: palette.sub }}>
                            {(data.jobs || []).length} active {((data.jobs || []).length === 1 ? "role" : "roles")}
                        </div>
                    </div>

                    {tabs.length > 0 && (
                        <div className="pw2-tabs-scroll" style={{ marginBottom: 12 }} data-testid="embed-v2-tabs">
                            <button type="button" onClick={() => onTabChange("")} className={`pw2-dept ${!activeTab ? "active" : ""}`} data-testid="embed-v2-tab-all">All · {(data.jobs || []).length}</button>
                            {tabs.map((tb) => {
                                const count = (data.jobs || []).filter((j) => tabMatch(j, tb)).length;
                                return (
                                    <button
                                        key={tb.id}
                                        type="button"
                                        onClick={() => onTabChange(tb.id)}
                                        className={`pw2-dept ${activeTab === tb.id ? "active" : ""}`}
                                        data-testid={`embed-v2-tab-${tb.id}`}
                                    >
                                        {tb.title}{tb._autoDept ? "" : ` · ${count}`}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {visible.length === 0 ? (
                        <div style={{ border: `1px dashed ${palette.border}`, padding: 28, textAlign: "center", color: palette.sub }}>
                            No active openings right now.
                        </div>
                    ) : (spotlightSplit ? [
                            { key: "spot", label: "★ Spotlight", jobs: spotlightSplit.spot, testid: "embed-v2-spotlight-section" },
                            ...(spotlightSplit.other.length > 0 ? [{ key: "other", label: "Other openings", jobs: spotlightSplit.other, testid: "embed-v2-openings-section" }] : []),
                        ] : [{ key: "all", label: null, jobs: visible, testid: null }]).map((section, si) => (
                        <div key={section.key} data-testid={section.testid || undefined} style={si > 0 ? { marginTop: 20 } : undefined}>
                            {section.label && (
                                <div style={{ margin: "4px 0 10px", fontSize: 10, fontFamily: "ui-monospace, monospace", textTransform: "uppercase", letterSpacing: ".18em", color: palette.sub }}>
                                    {section.label} · {section.jobs.length}
                                </div>
                            )}
                            <div className={layout === "grid" ? "pw2-grid" : ""}>
                                {section.jobs.map((j) => {
                                // Bug + feature: highlight_color was stripped
                                // from the projection so recruiter-picked
                                // colours never rendered. `show_highlight_colors`
                                // lets employers hide the colour cue entirely.
                                const showColors = board?.show_highlight_colors !== false;
                                const highlightColor = (j.highlight && showColors) ? (j.highlight_color || "#FBBF24") : null;
                                const urgent = j.urgency && j.urgency !== "Flexible" && showField("urgency");
                                const tabQS = activeTab ? `&tab_id=${encodeURIComponent(activeTab)}` : "";
                                const href = `${window.location.origin}/j/${j.slug}?embed=board-${boardId.slice(0, 8)}&board_id=${boardId}${tabQS}`;
                                const meta = [
                                    showField("location") && j.location && { icon: <MapPin size={11} />, text: j.location },
                                    showField("work_mode") && j.work_mode && { icon: <Briefcase size={11} />, text: j.work_mode },
                                    showField("employment_type") && j.employment_type && { text: j.employment_type },
                                    showField("experience") && { text: `${j.experience_min || 0}–${j.experience_max || 0} yrs` },
                                    showField("department") && j.department && { icon: <Building2 size={11} />, text: j.department },
                                    showField("industry") && j.industry && { text: j.industry },
                                ].filter(Boolean);
                                const hasSalary = (j.salary_min || j.salary_max) && showField("salary");
                                return (
                                    <a
                                        key={j.id}
                                        href={href}
                                        rel="noopener"
                                        onClick={() => requestScrollToTop(j.id)}
                                        data-testid={`embed-v2-job-${j.id}`}
                                        data-highlighted={highlightColor ? "true" : undefined}
                                        className="pw2-card"
                                        style={{
                                            padding: 14,
                                            background: palette.card,
                                            border: highlightColor ? `2px solid ${highlightColor}` : `1px solid ${palette.border}`,
                                            boxShadow: highlightColor ? `0 0 0 4px ${highlightColor}20` : undefined,
                                            marginBottom: layout === "list" ? 10 : 0,
                                            position: "relative",
                                        }}
                                    >
                                        {highlightColor && (
                                            <span
                                                data-testid={`embed-v2-job-highlight-${j.id}`}
                                                style={{
                                                    position: "absolute", top: -10, left: 12,
                                                    fontSize: 9, fontFamily: "ui-monospace, monospace",
                                                    letterSpacing: ".14em", textTransform: "uppercase",
                                                    padding: "2px 8px", background: highlightColor,
                                                    color: "#111", fontWeight: 800, border: `1px solid ${highlightColor}`,
                                                }}
                                            >★ Featured</span>
                                        )}
                                        <div className="pw2-card-row">
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                                    {showField("title") && <div className="pw2-job-title" style={{ lineHeight: 1.2 }}>{j.title}</div>}
                                                    {urgent && (
                                                        <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".14em", padding: "2px 6px", border: `1px solid ${palette.accent}`, color: palette.accent }}>
                                                            <Clock size={9} style={{ display: "inline", marginRight: 3, verticalAlign: -1 }} />{j.urgency}
                                                        </span>
                                                    )}
                                                </div>
                                                {meta.length > 0 && (
                                                    <div className="pw2-meta-row" style={{ color: palette.sub, marginTop: 6 }}>
                                                        {meta.map((m, i) => (
                                                            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                                                                {m.icon} {m.text}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                {hasSalary && (
                                                    <div style={{ marginTop: 6, fontSize: 11.5, fontFamily: "ui-monospace, monospace", color: palette.sub, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                                        <Wallet size={11} />
                                                        {formatCompCurrency(j.salary_min, j.salary_currency, j.salary_period)} – {formatCompCurrency(j.salary_max, j.salary_currency, j.salary_period)}
                                                    </div>
                                                )}
                                                {showField("posted_at") && j.created_at && (
                                                    <div style={{ marginTop: 4, fontSize: 10.5, fontFamily: "ui-monospace, monospace", color: palette.sub }}>
                                                        Posted {new Date(j.created_at).toLocaleDateString()}
                                                    </div>
                                                )}
                                                {showField("skills") && (j.required_skills || []).length > 0 && (
                                                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 8 }}>
                                                        {(j.required_skills || []).slice(0, 6).map((s) => (
                                                            <span key={s} style={{ padding: "3px 7px", border: `1px solid ${palette.border}`, color: palette.sub, fontSize: 10, fontFamily: "ui-monospace, monospace", textTransform: "uppercase", letterSpacing: ".1em" }}>{s}</span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {showField("apply_cta") && (
                                                <span style={{ color: palette.accent, fontSize: 10, fontFamily: "ui-monospace, monospace", textTransform: "uppercase", letterSpacing: ".12em", display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                                                    Apply <ArrowRight size={11} />
                                                </span>
                                            )}
                                        </div>
                                    </a>
                                );
                            })}
                            </div>
                        </div>
                    ))}

                    {/* Talent pool CTA — only when company has TP enabled and the board hasn't opted out */}
                    {(() => {
                        const tp = board?.talent_pool || {};
                        const enabled = (tp.enabled !== false) && data.company?.talent_pool_enabled && data.company?.talent_pool_slug;
                        if (!enabled) return null;
                        return (
                            <div
                                style={{ marginTop: 20, padding: 16, border: `1px dashed ${palette.border}`, background: palette.card, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, borderRadius: t.card_radius || "0px" }}
                                data-testid="embed-v2-talent-pool-cta"
                            >
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontWeight: 800, fontSize: 15 }}>{tp.title || "Don't see a fit?"}</div>
                                    <div style={{ marginTop: 4, color: palette.sub, fontSize: 12.5, lineHeight: 1.55 }}>
                                        {tp.subtitle || `Submit your resume so ${data.company?.name || "the team"} can reach out when a matching role opens up.`}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setView("pool");
                                        // Scroll parent page so the talent-pool form is in view
                                        try { window.parent?.postMessage({ kind: "portalwiz:scroll-to-top", target: boardId }, "*"); } catch { /* ignore */ }
                                        postEvent("talent_pool_open", {});
                                    }}
                                    style={{ background: palette.accent, color: "#fff", fontWeight: 700, fontSize: 13, padding: "9px 14px", border: "none", cursor: "pointer", borderRadius: t.button_radius || "0px", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
                                    data-testid="embed-v2-talent-pool-open"
                                >
                                    <UserPlus size={13} /> {tp.cta_label || "Join talent pool"}
                                </button>
                            </div>
                        );
                    })()}

                    {showPB && (
                        <div style={{ marginTop: 18, textAlign: "right", fontSize: 11, color: palette.sub, fontFamily: "ui-monospace, monospace" }}>
                            Powered by{" "}
                            <a href="https://portalwiz.com/talent-solutions-for-employers/" target="_blank" rel="noopener noreferrer" onClick={trackBranding} style={{ color: palette.accent, textDecoration: "underline", fontWeight: 700 }}>
                                Portalwiz Talent Solutions
                            </a>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function Skeleton({ palette }) {
    return (
        <div>
            <div className="pw2-skel" style={{ width: 120 }} />
            <div className="pw2-skel" style={{ width: 260, height: 28, marginTop: 8 }} />
            <div style={{ display: "grid", gap: 12, marginTop: 18 }}>
                {[0, 1, 2].map((i) => (
                    <div key={i} style={{ padding: 16, border: `1px solid ${palette.border}`, background: palette.card }}>
                        <div className="pw2-skel" style={{ width: "55%", height: 18 }} />
                        <div className="pw2-skel" style={{ width: "85%", height: 11, marginTop: 10 }} />
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * Inline talent-pool form rendered inside the same iframe as the job board.
 * Submits to the existing /public/talent-pool/{slug}/submit endpoint so the
 * standalone /talent-pool/<slug> page keeps working too.
 */
function InlineTalentPool({ board, company, palette, onBack }) {
    const tp = board?.talent_pool || {};
    const t = board?.theme || {};
    const slug = company?.talent_pool_slug;
    const fileRef = useRef(null);
    const [form, setForm] = useState({ name: "", email: "", phone: "", location: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!fileRef.current?.files?.[0]) return alert("Please attach your resume");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("name", form.name);
            fd.append("email", form.email);
            if (form.phone) fd.append("phone", form.phone);
            if (form.location) fd.append("location", form.location);
            fd.append("file", fileRef.current.files[0]);
            await publicApi.post(`/public/talent-pool/${slug}/submit`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 180000,
            });
            setDone(true);
        } catch (err) {
            alert(formatApiError(err));
        } finally { setBusy(false); }
    };

    const inputStyle = {
        width: "100%", height: 40, padding: "0 10px", border: `1px solid ${palette.border}`,
        background: palette.card, color: palette.text, fontSize: 14, outline: "none", borderRadius: t.button_radius || "0px",
    };

    if (done) {
        return (
            <div style={{ padding: 24, border: `1px solid ${palette.border}`, background: palette.card, borderRadius: t.card_radius || "0px" }} data-testid="embed-v2-pool-success">
                <CheckCircle2 size={32} color="#16a34a" />
                <h2 style={{ fontWeight: 900, fontSize: 22, margin: "10px 0 6px" }}>You're in the talent pool</h2>
                <p style={{ color: palette.sub, fontSize: 14, lineHeight: 1.55, margin: 0 }}>
                    Thanks — we've saved your profile to {company?.name || "the company"}'s talent pool. The team will be in touch when a matching role opens up.
                </p>
                <button
                    type="button"
                    onClick={onBack}
                    style={{ marginTop: 18, padding: "8px 14px", border: `1px solid ${palette.border}`, background: palette.bg, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, borderRadius: t.button_radius || "0px" }}
                    data-testid="embed-v2-pool-back"
                >
                    <ChevronLeft size={13} /> Back to all jobs
                </button>
            </div>
        );
    }

    return (
        <div data-testid="embed-v2-pool-form">
            <button
                type="button"
                onClick={onBack}
                style={{ background: "none", border: "none", color: palette.sub, cursor: "pointer", fontSize: 12, marginBottom: 12, display: "inline-flex", alignItems: "center", gap: 4 }}
                data-testid="embed-v2-pool-back-top"
            >
                <ChevronLeft size={12} /> All jobs
            </button>
            <h1 className="pw2-title" style={{ fontWeight: 900, letterSpacing: "-0.02em", margin: 0 }}>
                {tp.form_title || `Join the ${company?.name || ""} talent pool`}
            </h1>
            <p style={{ color: palette.sub, fontSize: 14, lineHeight: 1.55, margin: "10px 0 16px" }}>
                {tp.form_subtitle || company?.talent_pool_intro || "Send us your resume — we'll keep it on file and reach out when a matching role opens up."}
            </p>
            <form onSubmit={submit} style={{ display: "grid", gap: 10, maxWidth: 520 }}>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" style={inputStyle} data-testid="embed-v2-pool-name" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" style={inputStyle} data-testid="embed-v2-pool-email" />
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" style={inputStyle} data-testid="embed-v2-pool-phone" />
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City (optional)" style={inputStyle} data-testid="embed-v2-pool-location" />
                <div style={{ marginTop: 4 }}>
                    <div style={{ fontSize: 11, fontFamily: "ui-monospace, monospace", textTransform: "uppercase", letterSpacing: ".14em", color: palette.sub, marginBottom: 6 }}>Resume (PDF / DOC / DOCX / TXT)</div>
                    <input ref={fileRef} type="file" required accept=".pdf,.doc,.docx,.txt" style={{ fontSize: 13 }} data-testid="embed-v2-pool-file" />
                </div>
                <button
                    type="submit"
                    disabled={busy}
                    style={{ marginTop: 6, background: palette.accent, color: "#fff", fontWeight: 700, fontSize: 14, padding: "11px 16px", border: "none", cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1, borderRadius: t.button_radius || "0px" }}
                    data-testid="embed-v2-pool-submit"
                >
                    {busy ? "Submitting…" : "Submit resume →"}
                </button>
            </form>
        </div>
    );
}
